'use client'

import Link from 'next/link'

import { quickStartNavUrlPathForSection } from '@/lib/github/tutorialSlug'
import type { TutorialSection } from '@/lib/github/types'
import { ACADEMY_ROUTES } from '@/shared/academy.const'
import { TUTORIAL_MENU_TEST_IDS } from '@/shared/academy-test-ids.const'
import { useQuickStartLanguageContext } from '@/shared/components/QuickStartLayoutShell/context/quickStartLanguageContext'

import { TUTORIAL_MENU_DEFAULTS } from './tutorial-menu.const'

import styles from './TutorialMenu.module.scss'

const ArrowLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

interface TutorialMenuProps {
  sections: TutorialSection[]
  currentPath?: string
  basePath?: string
  title?: string
  showCompletion?: boolean
  linkScroll?: boolean
}

export function TutorialMenu({
  sections,
  currentPath,
  basePath,
  title,
  showCompletion = false,
  linkScroll = true,
}: TutorialMenuProps) {
  const { selectedLanguage } = useQuickStartLanguageContext()
  const resolvedBasePath = basePath ?? TUTORIAL_MENU_DEFAULTS.basePath
  const resolvedTitle = title ?? TUTORIAL_MENU_DEFAULTS.title

  const menuClassName = [styles.menu, showCompletion ? styles.withCompletion : styles.textOnly]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={menuClassName} data-testid={TUTORIAL_MENU_TEST_IDS.root}>
      <Link href={ACADEMY_ROUTES.home} className={styles.backButton}>
        <ArrowLeftIcon />
        <span>Back to Home</span>
      </Link>

      <div className={styles.header}>
        <Link href={resolvedBasePath} className={styles.titleLink}>
          <h3 className={styles.title}>{resolvedTitle}</h3>
        </Link>
      </div>

      <ul className={styles.list}>
        {sections.map((section) => {
          const isActive = currentPath === section.path

          return (
            <li key={section.id} className={`${styles.item} ${isActive ? styles.active : ''}`}>
              <Link
                href={`${resolvedBasePath}/${quickStartNavUrlPathForSection(section, selectedLanguage ?? undefined)}`}
                className={styles.link}
                scroll={linkScroll}
                aria-current={isActive ? 'page' : undefined}
              >
                <span className={styles.text}>{section.title}</span>
              </Link>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
